import classNames from "classnames/bind";
import React, { createElement, ReactElement, useEffect, useState } from "react";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import Footer from "../Footer/Footer";
import { Grid } from "../Grid";
import ReactLoader from "../Loader/ReactLoader";
import { Tab, TabContentPanel } from "../Tabs";
import type { TabProps } from "../Tabs/types";
import Header from "./Header";
import styles from "./Page.module.scss";
import type { HeaderProps, PageProps } from "./types";

const cx = classNames.bind(styles);

const Page = ({
  title,
  setDocumentTitle = true,
  documentTitleAppName = "Validere",
  children,
  rightSlot,
  category,
  className,
  renderMeta,
  shouldCenterContent = false,
  shouldContentBeFullWidth = false,
  isLoading = false,
  breadcrumbs,
  actionRow,
  footer,
  footerAlign = "right",
  activeTabKey,
  onActiveTabKeyChange,
  contentRef,
  error,
  onErrorRender,
}: PageProps) => {
  const [tabs, setTabs] = useState<Array<ReactElement<TabProps>>>();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const childArray = (
      Array.isArray(children) ? children : [children]
    ).flatMap((child) => (Array.isArray(child) ? child : [child]));

    const tabChildren = childArray.filter(
      (child) => (child as React.ReactElement)?.type === createElement(Tab).type
    );

    if (tabChildren?.length) {
      setTabs(tabChildren);
    }
  }, [children]);

  const headerProps: HeaderProps = {
    title,
    setDocumentTitle,
    documentTitleAppName,
    category,
    renderMeta,
    breadcrumbs,
    actionRow,
    tabs,
    activeTabKey,
    onActiveTabKeyChange,
    isLoading,
  };

  return (
    <div
      className={cx("page", className)}
      aria-label="page"
      aria-busy={isLoading}
    >
      {breadcrumbs?.length ? (
        <div className={cx("breadcrumbContainer")}>
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            isLoading={isLoading && !error}
          />
        </div>
      ) : null}

      <div className={cx("pageContent")}>
        <div
          className={cx("scrollWindow", {
            centerContent: shouldCenterContent,
          })}
        >
          {title && !error ? <Header {...headerProps} /> : null}

          <main
            className={cx("main", {
              centerContent: shouldCenterContent,
            })}
            role="main"
            ref={contentRef}
          >
            {!error ? (
              <Grid
                className={cx("grid", {
                  centerContent: shouldCenterContent,
                  fullWidth: shouldContentBeFullWidth,
                })}
              >
                <ReactLoader
                  loaded={!isLoading}
                  position={50}
                >
                  {tabs?.length ? (
                    <TabContentPanel
                      tabs={tabs}
                      activeKey={activeTabKey}
                    />
                  ) : (
                    children
                  )}
                </ReactLoader>
              </Grid>
            ) : (
              onErrorRender?.({ error })
            )}
          </main>
        </div>
        {!error && rightSlot ? rightSlot : null}
      </div>

      {footer && !error ? <Footer align={footerAlign}>{footer}</Footer> : null}
    </div>
  );
};

export default Page;
